// src/components/admin/TeamMemberManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import ImageUploader from './page-management/ImageUploader';

// Import icons
import { 
  Save, 
  Trash2, 
  PlusCircle, 
  AlertCircle,
  UserPlus,
  Users,
  ChevronRight,
  ArrowLeft,
  Check,
  X,
  Camera,
  Link,
  Plus,
  Minus
} from 'lucide-react';


const TeamMemberManagement = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [currentMember, setCurrentMember] = useState(null);
  const [currentMemberId, setCurrentMemberId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showForm, setShowForm] = useState(false);

  
  
  // Form data for team member
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    department: '',
    photoUrl: '',
    bio: '',
    shortBio: '',
    email: '',
    phone: '',
    isLeadership: false,
    isActive: true,
    socialLinks: [],
    skills: [],
    credentials: [],
    education: [],
    achievements: []
  });

  // For managing arrays in the form
  const [newSkill, setNewSkill] = useState('');
  const [newCredential, setNewCredential] = useState('');
  const [newEducation, setNewEducation] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [newSocialLink, setNewSocialLink] = useState({ platform: 'linkedin', url: '' });



const handleDragStart = (e, index) => { e.dataTransfer.setData('text/plain', index); };

const allowDrop = (e) => { e.preventDefault(); };

const handleDrop = async (e, dropIndex) => {
     e.preventDefault(); 
     const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
     const updatedTeamMembers = [...teamMembers];
     const draggedMember = updatedTeamMembers.splice(dragIndex, 1)[0];
     updatedTeamMembers.splice(dropIndex, 0, draggedMember);
     setTeamMembers(updatedTeamMembers); 
     try {
        await axios.post('/api/team-members/reorder', {
             memberIds: updatedTeamMembers.map(member => member._id) }); 
     } catch (err) {
         console.error('Error reordering team members:', err);
          fetchTeamMembers(); // reload from server if needed
 } };

  // Check admin status
  useEffect(() => {
    if (!user || !user.isAdmin) {
      // Instead of navigating, just show an error or empty state
      setError('You do not have permission to access this section');
      setLoading(false);
    }
  }, [user]);

  // Fetch all team members
  const fetchTeamMembers = useCallback(async () => {
    try {
      const response = await axios.get('/api/team-members');
      setTeamMembers(response.data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError('Failed to load team members. Please try again.');
    }
  }, []);

  // Fetch a specific team member by ID
  const fetchTeamMember = useCallback(async (memberId) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/team-members/${memberId}`);

      setCurrentMember(response.data);
      setFormData({
        name: response.data.name || '',
        position: response.data.position || '',
        department: response.data.department || '',
        photoUrl: response.data.photoUrl || '',
        bio: response.data.bio || '',
        shortBio: response.data.shortBio || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        isLeadership: response.data.isLeadership || false,
        isActive: response.data.isActive !== false, // Default to true
        socialLinks: response.data.socialLinks || [],
        skills: response.data.skills || [],
        credentials: response.data.credentials || [],
        education: response.data.education || [],
        achievements: response.data.achievements || []
      });
      setError(null);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching team member:', err);
      setError('Failed to load team member details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    fetchTeamMembers();
    if (currentMemberId) {
      fetchTeamMember(currentMemberId);
    } else {
      setCurrentMember(null);
      setLoading(false);
    }
  }, [currentMemberId, fetchTeamMembers, fetchTeamMember]);

  // Handler for editing a team member
  const handleEditMember = (memberId) => {
    setCurrentMemberId(memberId);
    fetchTeamMember(memberId);
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle image upload
  const handleImageUpload = (imagePath) => {
    setFormData({
      ...formData,
      photoUrl: imagePath
    });
  };

  // Array management helpers
  const addArrayItem = (array, item, setter) => {
    if (!item.trim()) return;
    
    setFormData({
      ...formData,
      [array]: [...formData[array], item]
    });
    setter('');
  };

  const removeArrayItem = (array, index) => {
    const newArray = [...formData[array]];
    newArray.splice(index, 1);
    setFormData({
      ...formData,
      [array]: newArray
    });
  };

  // Social links management
  const addSocialLink = () => {
    if (!newSocialLink.url.trim()) return;
    
    setFormData({
      ...formData,
      socialLinks: [...formData.socialLinks, { ...newSocialLink }]
    });
    setNewSocialLink({ platform: 'linkedin', url: '' });
  };

  const removeSocialLink = (index) => {
    const newLinks = [...formData.socialLinks];
    newLinks.splice(index, 1);
    setFormData({
      ...formData,
      socialLinks: newLinks
    });
  };

  const updateSocialLink = (index, field, value) => {
    const newLinks = [...formData.socialLinks];
    newLinks[index] = {
      ...newLinks[index],
      [field]: value
    };
    setFormData({
      ...formData,
      socialLinks: newLinks
    });
  };

  // Save/update team member
  const saveTeamMember = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      let response;
      
      if (currentMember) {
        // Update existing team member
        response = await axios.put(`/api/team-members/${currentMember._id}`, formData);
        setCurrentMember(response.data);
        setSuccess('Team member updated successfully!');
      } else {
        // Create new team member
        response = await axios.post('/api/team-members', formData);
        setCurrentMember(response.data);
        setCurrentMemberId(response.data._id);
        setSuccess('Team member created successfully!');
      }
      
      await fetchTeamMembers();
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error saving team member:', err);
      setError('Failed to save team member. Please check the form and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Delete team member
  const deleteTeamMember = async () => {
    if (!currentMember) return;
    
    if (!window.confirm(`Are you sure you want to delete ${currentMember.name}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setLoading(true);
      await axios.delete(`/api/team-members/${currentMember._id}`);
      await fetchTeamMembers();
      setCurrentMemberId(null);
      setCurrentMember(null);
      setError(null);
      setSuccess('Team member deleted successfully!');
      setShowForm(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error deleting team member:', err);
      setError('Failed to delete team member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Back to team members list 
  const handleBackToList = () => {
    setCurrentMemberId(null);
    setShowForm(false);
  };
  
  // Clear notification messages
  const clearNotifications = () => {
    setError(null);
    setSuccess(null);
  };

  // Create a new team member
  const handleNewTeamMember = () => {
    setCurrentMember(null);
    setCurrentMemberId(null);
    setFormData({
      name: '',
      position: '',
      department: '',
      photoUrl: '',
      bio: '',
      shortBio: '',
      email: '',
      phone: '',
      isLeadership: false,
      isActive: true,
      socialLinks: [],
      skills: [],
      credentials: [],
      education: [],
      achievements: []
    });
    setShowForm(true);
  };

  // Render Social Platform Icon
  const SocialIcon = ({ platform }) => {
    switch (platform) {
      case 'linkedin':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
      case 'twitter':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>;
      case 'facebook':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
      case 'instagram':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>;
      case 'github':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>;
      case 'website':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M21.41 8.64v-.05a10 10 0 1 0-18.82 0v.05a9.86 9.86 0 0 0 0 6.72v.05a10 10 0 0 0 18.82 0v-.05a9.86 9.86 0 0 0 0-6.72zM4.26 14a7.82 7.82 0 0 1 0-4h1.86a16.73 16.73 0 0 0 0 4zm.82 2h1.4a12.15 12.15 0 0 0 1 2.57A8 8 0 0 1 5.08 16zm1.4-8h-1.4a8 8 0 0 1 2.37-2.57A12.15 12.15 0 0 0 6.48 8zM11 19.7A6.34 6.34 0 0 1 8.57 14H11zm0-7.7H8.14a14.36 14.36 0 0 1 0-4H11zm0-6H8.57A6.34 6.34 0 0 1 11 4.3zm7.92 0h-1.4a12.15 12.15 0 0 0-1-2.57A8 8 0 0 1 18.92 6zM13 4.3A6.34 6.34 0 0 1 15.43 10H13zm0 15.4V16h2.43A6.34 6.34 0 0 1 13 19.7zm2.86-5.7a14.36 14.36 0 0 1 0-4h2.86a14.36 14.36 0 0 1 0 4zm.29 5.27a12.15 12.15 0 0 0 1-2.57h1.4a8 8 0 0 1-2.4 2.57zM19.74 14h-1.86a16.73 16.73 0 0 0 0-4h1.86a7.82 7.82 0 0 1 0 4z"/></svg>;
      case 'email':
        return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12.713l-11.985-9.713h23.97l-11.985 9.713zm0 2.574l-12-9.725v15.438h24v-15.438l-12 9.725z"/></svg>;
      default:
        return <Link className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <h2 className="text-2xl font-semibold">Team Member Management</h2>
      <div className="bg-gray-50 min-h-screen rounded-2xl">

      {/* Notification area - always visible */}
      <div className="container mx-auto px-4 py-2">
        {error && (
          <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p>{error}</p>
            </div>
            <button 
              onClick={clearNotifications}
              className="text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
            <p>{success}</p>
            <button 
              onClick={clearNotifications}
              className="text-green-500 hover:text-green-700"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-64 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {!showForm ? (
            /* Team Members List View */
            <div className="container mx-auto p-1">
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-semibold text-gray-800">Team Members</h1>
                    <button
                      onClick={handleNewTeamMember}
                      className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors"
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
                    </button>
                  </div>
                </div>
                
                {teamMembers.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                      <Users className="w-8 h-8 text-blue-500" />
                    </div>
                    <h2 className="text-xl font-medium text-gray-800 mb-2">No Team Members Found</h2>
                    <p className="text-gray-600 mb-6">Add your first team member to get started.</p>
                    <button
                      onClick={handleNewTeamMember}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors mx-auto"
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
                    </button>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200"> 
                    {teamMembers.map((member, index) => ( 
                      <div 
                        key={member._id} 
                        draggable 
                        onDragStart={(e) => handleDragStart(e, index)} 
                        onDragOver={allowDrop} 
                        onDrop={(e) => handleDrop(e, index)} 
                        className="p-4 hover:bg-gray-50 transition-colors cursor-move"
                      > 
                        <div className="sm:flex justify-between items-center"> 
                          <div className="mb-2 sm:mb-0 flex items-center"> 
                            {member.photoUrl ? ( 
                              <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0"> 
                                <img src={member.photoUrl} alt={member.name} className="w-full h-full object-cover" /> 
                              </div> 
                            ) : ( 
                              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4 flex-shrink-0"> 
                                <Users className="w-6 h-6 text-gray-500" /> 
                              </div> 
                            )}
                            <div> 
                              <h3 className="font-medium text-gray-900">{member.name}</h3> 
                              <div className="text-sm text-gray-500 flex flex-wrap items-center mt-1"> 
                                <span className="mr-3">{member.position}</span> 
                                {member.isLeadership && ( 
                                  <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium mr-2"> 
                                    Leadership 
                                  </span> 
                                )} 
                                {!member.isActive && ( 
                                  <span className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs font-medium"> 
                                    Inactive 
                                  </span> 
                                )} 
                              </div> 
                            </div> 
                          </div>
                          <button 
                            onClick={() => handleEditMember(member._id)} 
                            style={{transform: 'none', boxShadow:'none'}}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                          > 
                            Edit <ChevronRight className="w-4 h-4 ml-1" /> 
                          </button> 
                        </div> 
                      </div> 
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Team Member Form View */
            <div className="container mx-auto px-4 py-6">
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                    <div className="flex items-center">
                      <button
                        onClick={handleBackToList}
                        className="mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </button>
                      <h1 className="text-2xl font-semibold text-gray-800">
                        {currentMember ? `Edit: ${currentMember.name}` : 'Add New Team Member'}
                      </h1>
                    </div>
                    <div className="flex space-x-2 w-full sm:w-auto justify-end">
                    {currentMember && (
                        <button
                          onClick={deleteTeamMember}
                          className="bg-red-500 font-normal hover:bg-red-600 text-white px-3 py-1 rounded inline-flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-1" /> Delete
                        </button>
                      )}
                      <button
                        onClick={saveTeamMember}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
                      >
                        <Save className="w-4 h-4 mr-1" /> Save
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Form Content */}
                <form onSubmit={saveTeamMember} className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Left Column - Basic Info */}
                    <div className="space-y-6">
                      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h2>
                      
                      {/* Photo Upload */}
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
                        <div className="flex items-start">
                          <div className="mr-4">
                            {formData.photoUrl ? (
                              <div className="relative group w-24 h-24 rounded-full overflow-hidden border border-gray-200">
                                <img 
                                  src={formData.photoUrl} 
                                  alt={formData.name} 
                                  className="w-full h-full object-cover"
                                />
                                <div 
                                  className="absolute inset-0 bg-black bg-opacity-40 group-hover:flex items-center justify-center hidden"
                                  onClick={() => setFormData({...formData, photoUrl: ''})}
                                >
                                  <X className="w-6 h-6 text-white" />
                                </div>
                              </div>
                            ) : (
                              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
                                <Camera className="w-8 h-8 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <ImageUploader 
                              onImageUpload={handleImageUpload} 
                              pageSlug="team-members" 
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Name */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
                          Name*
                        </label>
                        <input
                          id="name"
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      {/* Position */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="position">
                          Position*
                        </label>
                        <input
                          id="position"
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          required
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      {/* Department */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="department">
                          Department
                        </label>
                        <input
                          id="department"
                          type="text"
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      {/* Email */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                          Email
                        </label>
                        <input
                          id="email"
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      {/* Phone */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
                          Phone
                        </label>
                        <input
                          id="phone"
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      
                      {/* Flags */}
                      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
                        <label className="flex items-center">
                          <input 
                            type="checkbox"
                            name="isLeadership"
                            checked={formData.isLeadership}
                            onChange={handleInputChange}
                            className="mr-2 h-4 w-4 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">Leadership Team</span>
                        </label>
                        <label className="flex items-center">
                          <input 
                            type="checkbox"
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleInputChange}
                            className="mr-2 h-4 w-4 text-blue-600"
                          />
                          <span className="text-sm text-gray-700">Active</span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Right Column - Detailed Info */}
                    <div className="space-y-6">
                      <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Detailed Information</h2>
                      
                      {/* Short Bio */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="shortBio">
                          Short Bio (max 600 chars)
                        </label>
                        <textarea
                          id="shortBio"
                          name="shortBio"
                          value={formData.shortBio}
                          onChange={handleInputChange}
                          rows="2"
                          maxLength="600"
                          className="w-full p-2 border border-gray-300 rounded-md"
                        ></textarea>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.shortBio?.length || 0}/600 characters
                        </p>
                      </div>
                      
                      {/* Full Bio */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="bio">
                          Full Bio
                        </label>
                        <textarea
                          id="bio"
                          name="bio"
                          value={formData.bio}
                          onChange={handleInputChange}
                          rows="6"
                          className="w-full p-2 border border-gray-300 rounded-md"
                        ></textarea>
                      </div>
                      
                      {/* Social Links */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Social Links
                        </label>
                        
                        {formData.socialLinks.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {formData.socialLinks.map((link, index) => (
                              <div key={index} className="flex items-center bg-gray-50 p-2 rounded">
                                <div className="mr-2 text-gray-500">
                                  <SocialIcon platform={link.platform} />
                                </div>
                                <span className="text-sm flex-1 truncate">{link.url}</span>
                                <button
                                  type="button"
                                  className="text-red-500 hover:text-red-700 p-1"
                                  onClick={() => removeSocialLink(index)}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="flex items-end space-x-2">
                          <div className="flex-shrink-0">
                            <label className="block text-xs text-gray-500 mb-1">Platform</label>
                            <select
                              value={newSocialLink.platform}
                              onChange={(e) => setNewSocialLink({...newSocialLink, platform: e.target.value})}
                              className="p-2 border border-gray-300 rounded-md text-sm"
                            >
                              <option value="linkedin">LinkedIn</option>
                              <option value="twitter">Twitter</option>
                              <option value="facebook">Facebook</option>
                              <option value="instagram">Instagram</option>
                              <option value="github">GitHub</option>
                              <option value="website">Website</option>
                              <option value="email">Email</option>
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">URL</label>
                            <input
                              type="text"
                              value={newSocialLink.url}
                              onChange={(e) => setNewSocialLink({...newSocialLink, url: e.target.value})}
                              placeholder="https://..."
                              className="w-full p-2 border border-gray-300 rounded-md text-sm"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={addSocialLink}
                            className="bg-blue-100 text-blue-700 hover:bg-blue-200 p-2 rounded-md"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Skills */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Skills
                        </label>
                        
                        <div className="flex flex-wrap gap-2 mb-2">
                          {formData.skills.map((skill, index) => (
                            <div 
                              key={index} 
                              className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-sm flex items-center"
                            >
                              <span>{skill}</span>
                              <button
                                type="button"
                                className="ml-1 text-blue-500 hover:text-blue-700"
                                onClick={() => removeArrayItem('skills', index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex">
                          <input
                            type="text"
                            value={newSkill}
                            onChange={(e) => setNewSkill(e.target.value)}
                            placeholder="Add a skill"
                            className="flex-1 p-2 border border-gray-300 rounded-l-md"
                          />
                          <button
                            type="button"
                            onClick={() => addArrayItem('skills', newSkill, setNewSkill)}
                            className="bg-blue-500 text-white px-3 rounded-r-md"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Credentials */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Credentials
                        </label>
                        
                        <div className="space-y-1 mb-2">
                          {formData.credentials.map((credential, index) => (
                            <div 
                              key={index} 
                              className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
                            >
                              <span>{credential}</span>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => removeArrayItem('credentials', index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex">
                          <input
                            type="text"
                            value={newCredential}
                            onChange={(e) => setNewCredential(e.target.value)}
                            placeholder="Add a credential (e.g., MBA, CPA)"
                            className="flex-1 p-2 border border-gray-300 rounded-l-md"
                          />
                          <button
                            type="button"
                            onClick={() => addArrayItem('credentials', newCredential, setNewCredential)}
                            className="bg-blue-500 text-white px-3 rounded-r-md"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Education */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Education
                        </label>
                        
                        <div className="space-y-1 mb-2">
                          {formData.education.map((edu, index) => (
                            <div 
                              key={index} 
                              className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
                            >
                              <span>{edu}</span>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => removeArrayItem('education', index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex">
                          <input
                            type="text"
                            value={newEducation}
                            onChange={(e) => setNewEducation(e.target.value)}
                            placeholder="Add education (e.g., BS Computer Science, Harvard)"
                            className="flex-1 p-2 border border-gray-300 rounded-l-md"
                          />
                          <button
                            type="button"
                            onClick={() => addArrayItem('education', newEducation, setNewEducation)}
                            className="bg-blue-500 text-white px-3 rounded-r-md"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Achievements */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Achievements
                        </label>
                        
                        <div className="space-y-1 mb-2">
                          {formData.achievements.map((achievement, index) => (
                            <div 
                              key={index} 
                              className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
                            >
                              <span>{achievement}</span>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => removeArrayItem('achievements', index)}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex">
                          <input
                            type="text"
                            value={newAchievement}
                            onChange={(e) => setNewAchievement(e.target.value)}
                            placeholder="Add an achievement"
                            className="flex-1 p-2 border border-gray-300 rounded-l-md"
                          />
                          <button
                            type="button"
                            onClick={() => addArrayItem('achievements', newAchievement, setNewAchievement)}
                            className="bg-blue-500 text-white px-3 rounded-r-md"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Form Buttons */}
                  <div className="mt-8 border-t pt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={handleBackToList}
                      className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-4 py-2 rounded-md mr-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md"
                    >
                      {currentMember ? 'Update Team Member' : 'Create Team Member'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
        
      )}
    </div>
    </div>
  );
};

export default TeamMemberManagement;
















// // seperate page
// // src/components/admin/TeamMemberManagement.js
// import React, { useState, useEffect, useCallback } from 'react';
// import { useParams, useNavigate, useLocation } from 'react-router-dom';
// import axios from 'axios';
// import { useAuth } from '../../context/AuthContext';
// import ImageUploader from './page-management/ImageUploader';
// // import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// // Import icons
// import { 
//   Save, 
//   Trash2, 
//   PlusCircle, 
//   AlertCircle,
//   UserPlus,
//   Users,
//   ChevronRight,
//   ArrowLeft,
//   Check,
//   X,
//   Camera,
//   Link,
//   Plus,
//   Minus
// } from 'lucide-react';


// const TeamMemberManagement = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const location = useLocation();
//   const { user } = useAuth();
//   const [loading, setLoading] = useState(true);
//   const [teamMembers, setTeamMembers] = useState([]);
//   const [currentMember, setCurrentMember] = useState(null);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(null);
//   const [showForm, setShowForm] = useState(false);

  
  
//   // Form data for team member
//   const [formData, setFormData] = useState({
//     name: '',
//     position: '',
//     department: '',
//     photoUrl: '',
//     bio: '',
//     shortBio: '',
//     email: '',
//     phone: '',
//     isLeadership: false,
//     isActive: true,
//     socialLinks: [],
//     skills: [],
//     credentials: [],
//     education: [],
//     achievements: []
//   });

//   // For managing arrays in the form
//   const [newSkill, setNewSkill] = useState('');
//   const [newCredential, setNewCredential] = useState('');
//   const [newEducation, setNewEducation] = useState('');
//   const [newAchievement, setNewAchievement] = useState('');
//   const [newSocialLink, setNewSocialLink] = useState({ platform: 'linkedin', url: '' });



// const handleDragStart = (e, index) => { e.dataTransfer.setData('text/plain', index); };

// const allowDrop = (e) => { e.preventDefault(); };

// const handleDrop = async (e, dropIndex) => {
//      e.preventDefault(); 
//      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
//      const updatedTeamMembers = [...teamMembers];
//      const draggedMember = updatedTeamMembers.splice(dragIndex, 1)[0];
//      updatedTeamMembers.splice(dropIndex, 0, draggedMember);
//      setTeamMembers(updatedTeamMembers); 
//      try {
//         await axios.post('/api/team-members/reorder', {
//              memberIds: updatedTeamMembers.map(member => member._id) }); 
//      } catch (err) {
//          console.error('Error reordering team members:', err);
//           fetchTeamMembers(); // reload from server if needed
//  } };

//   // Check admin status
//   useEffect(() => {
//     if (!user || !user.isAdmin) {
//       navigate('/login', { state: { from: location.pathname } });
//     }
//   }, [user, navigate, location]);

//   // Fetch all team members
//   const fetchTeamMembers = useCallback(async () => {
//     try {
//       const response = await axios.get('/api/team-members');
//       setTeamMembers(response.data);
//     } catch (err) {
//       console.error('Error fetching team members:', err);
//       setError('Failed to load team members. Please try again.');
//     }
//   }, []);

//   // Fetch a specific team member by ID
//   const fetchTeamMember = useCallback(async (memberId) => {
//     try {
//       setLoading(true);
//       const response = await axios.get(`/api/team-members/${memberId}`);

//       setCurrentMember(response.data);
//       setFormData({
//         name: response.data.name || '',
//         position: response.data.position || '',
//         department: response.data.department || '',
//         photoUrl: response.data.photoUrl || '',
//         bio: response.data.bio || '',
//         shortBio: response.data.shortBio || '',
//         email: response.data.email || '',
//         phone: response.data.phone || '',
//         isLeadership: response.data.isLeadership || false,
//         isActive: response.data.isActive !== false, // Default to true
//         socialLinks: response.data.socialLinks || [],
//         skills: response.data.skills || [],
//         credentials: response.data.credentials || [],
//         education: response.data.education || [],
//         achievements: response.data.achievements || []
//       });
//       setError(null);
//       setShowForm(true);
//     } catch (err) {
//       console.error('Error fetching team member:', err);
//       setError('Failed to load team member details. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   }, []);

//   // Load data on component mount
//   useEffect(() => {
//     fetchTeamMembers();
//     if (id) {
//       fetchTeamMember(id);
//     } else {
//       setCurrentMember(null);
//       setLoading(false);
//       setShowForm(false);
//     }
//   }, [id, fetchTeamMembers, fetchTeamMember]);

//   // Handle input changes
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData({
//       ...formData,
//       [name]: type === 'checkbox' ? checked : value
//     });
//   };

//   // Handle image upload
//   const handleImageUpload = (imagePath) => {
//     setFormData({
//       ...formData,
//       photoUrl: imagePath
//     });
//   };

//   // Array management helpers
//   const addArrayItem = (array, item, setter) => {
//     if (!item.trim()) return;
    
//     setFormData({
//       ...formData,
//       [array]: [...formData[array], item]
//     });
//     setter('');
//   };

//   const removeArrayItem = (array, index) => {
//     const newArray = [...formData[array]];
//     newArray.splice(index, 1);
//     setFormData({
//       ...formData,
//       [array]: newArray
//     });
//   };

//   // Social links management
//   const addSocialLink = () => {
//     if (!newSocialLink.url.trim()) return;
    
//     setFormData({
//       ...formData,
//       socialLinks: [...formData.socialLinks, { ...newSocialLink }]
//     });
//     setNewSocialLink({ platform: 'linkedin', url: '' });
//   };

//   const removeSocialLink = (index) => {
//     const newLinks = [...formData.socialLinks];
//     newLinks.splice(index, 1);
//     setFormData({
//       ...formData,
//       socialLinks: newLinks
//     });
//   };

//   const updateSocialLink = (index, field, value) => {
//     const newLinks = [...formData.socialLinks];
//     newLinks[index] = {
//       ...newLinks[index],
//       [field]: value
//     };
//     setFormData({
//       ...formData,
//       socialLinks: newLinks
//     });
//   };

//   // Save/update team member
//   const saveTeamMember = async (e) => {
//     e.preventDefault();
//     try {
//       setLoading(true);
      
//       let response;
      
//       if (currentMember) {
//         // Update existing team member
//         response = await axios.put(`/api/team-members/${currentMember._id}`, formData);
//         setCurrentMember(response.data);
//         setSuccess('Team member updated successfully!');
//       } else {
//         // Create new team member
//         response = await axios.post('/api/team-members', formData);
//         setCurrentMember(response.data);
//         navigate(`/admin/team-members/${response.data._id}`);
//         setSuccess('Team member created successfully!');
//       }
      
//       await fetchTeamMembers();
//       setError(null);
      
//       // Clear success message after 3 seconds
//       setTimeout(() => {
//         setSuccess(null);
//       }, 3000);
//     } catch (err) {
//       console.error('Error saving team member:', err);
//       setError('Failed to save team member. Please check the form and try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Delete team member
//   const deleteTeamMember = async () => {
//     if (!currentMember) return;
    
//     if (!window.confirm(`Are you sure you want to delete ${currentMember.name}? This action cannot be undone.`)) {
//       return;
//     }
    
//     try {
//       setLoading(true);
//       await axios.delete(`/api/team-members/${currentMember._id}`);
//       await fetchTeamMembers();
//       navigate('/admin/team-members');
//       setCurrentMember(null);
//       setError(null);
//       setSuccess('Team member deleted successfully!');
//       setShowForm(false);
      
//       // Clear success message after 3 seconds
//       setTimeout(() => {
//         setSuccess(null);
//       }, 3000);
//     } catch (err) {
//       console.error('Error deleting team member:', err);
//       setError('Failed to delete team member. Please try again.');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Handle reordering team members (drag and drop)
//   const handleDragEnd = async (result) => {
//     if (!result.destination) return;

//     const items = Array.from(teamMembers);
//     const [reorderedItem] = items.splice(result.source.index, 1);
//     items.splice(result.destination.index, 0, reorderedItem);
    
//     setTeamMembers(items);
    
//     try {
//       // Send the new order to the backend
//       await axios.post('/api/team-members/reorder', {
//         memberIds: items.map(member => member._id)
//       });
//     } catch (err) {
//       console.error('Error reordering team members:', err);
//       setError('Failed to update order. Please try again.');
//       fetchTeamMembers(); // Refresh from server to ensure consistency
//     }
//   };

//   // Clear notification messages
//   const clearNotifications = () => {
//     setError(null);
//     setSuccess(null);
//   };

//   // Create a new team member
//   const handleNewTeamMember = () => {
//     setCurrentMember(null);
//     setFormData({
//       name: '',
//       position: '',
//       department: '',
//       photoUrl: '',
//       bio: '',
//       shortBio: '',
//       email: '',
//       phone: '',
//       isLeadership: false,
//       isActive: true,
//       socialLinks: [],
//       skills: [],
//       credentials: [],
//       education: [],
//       achievements: []
//     });
//     setShowForm(true);
//   };

//   // Back to team members list 
//   const handleBackToList = () => {
//     navigate('/admin/team-members');
//     setShowForm(false);
//   };

//   // Render Social Platform Icon
//   const SocialIcon = ({ platform }) => {
//     switch (platform) {
//       case 'linkedin':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
//       case 'twitter':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>;
//       case 'facebook':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
//       case 'instagram':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"/></svg>;
//       case 'github':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>;
//       case 'website':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M21.41 8.64v-.05a10 10 0 1 0-18.82 0v.05a9.86 9.86 0 0 0 0 6.72v.05a10 10 0 0 0 18.82 0v-.05a9.86 9.86 0 0 0 0-6.72zM4.26 14a7.82 7.82 0 0 1 0-4h1.86a16.73 16.73 0 0 0 0 4zm.82 2h1.4a12.15 12.15 0 0 0 1 2.57A8 8 0 0 1 5.08 16zm1.4-8h-1.4a8 8 0 0 1 2.37-2.57A12.15 12.15 0 0 0 6.48 8zM11 19.7A6.34 6.34 0 0 1 8.57 14H11zm0-7.7H8.14a14.36 14.36 0 0 1 0-4H11zm0-6H8.57A6.34 6.34 0 0 1 11 4.3zm7.92 0h-1.4a12.15 12.15 0 0 0-1-2.57A8 8 0 0 1 18.92 6zM13 4.3A6.34 6.34 0 0 1 15.43 10H13zm0 15.4V16h2.43A6.34 6.34 0 0 1 13 19.7zm2.86-5.7a14.36 14.36 0 0 1 0-4h2.86a14.36 14.36 0 0 1 0 4zm.29 5.27a12.15 12.15 0 0 0 1-2.57h1.4a8 8 0 0 1-2.4 2.57zM19.74 14h-1.86a16.73 16.73 0 0 0 0-4h1.86a7.82 7.82 0 0 1 0 4z"/></svg>;
//       case 'email':
//         return <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12.713l-11.985-9.713h23.97l-11.985 9.713zm0 2.574l-12-9.725v15.438h24v-15.438l-12 9.725z"/></svg>;
//       default:
//         return <Link className="w-5 h-5" />;
//     }
//   };

//   return (
//     <div className="space-y-6">
//       {/* Header */}
//       <h2 className="text-2xl font-semibold">Team Member Managemnet</h2>
//       <div className="bg-gray-50 min-h-screen rounded-2xl">

//       {/* Notification area - always visible */}
//       <div className="container mx-auto px-4 py-2">
//         {error && (
//           <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md flex justify-between items-center">
//             <div className="flex items-center">
//               <AlertCircle className="w-5 h-5 mr-2" />
//               <p>{error}</p>
//             </div>
//             <button 
//               onClick={clearNotifications}
//               className="text-red-500 hover:text-red-700"
//             >
//               ×
//             </button>
//           </div>
//         )}
        
//         {success && (
//           <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md flex justify-between items-center">
//             <p>{success}</p>
//             <button 
//               onClick={clearNotifications}
//               className="text-green-500 hover:text-green-700"
//             >
//               ×
//             </button>
//           </div>
//         )}
//       </div>

//       {loading ? (
//         <div className="h-64 flex justify-center items-center">
//           <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
//         </div>
//       ) : (
//         <>
//           {!showForm ? (
//             /* Team Members List View */
//             <div className="container mx-auto px-4 py-6">
//               <div className="bg-white shadow-sm rounded-lg overflow-hidden">
//                 <div className="p-6 border-b border-gray-200">
//                   <div className="flex justify-between items-center">
//                     <h1 className="text-2xl font-semibold text-gray-800">Team Members</h1>
//                     <button
//                       onClick={handleNewTeamMember}
//                       className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors"
//                     >
//                       <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
//                     </button>
//                   </div>
//                 </div>
                
//                 {teamMembers.length === 0 ? (
//                   <div className="p-8 text-center">
//                     <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
//                       <Users className="w-8 h-8 text-blue-500" />
//                     </div>
//                     <h2 className="text-xl font-medium text-gray-800 mb-2">No Team Members Found</h2>
//                     <p className="text-gray-600 mb-6">Add your first team member to get started.</p>
//                     <button
//                       onClick={handleNewTeamMember}
//                       className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center shadow-sm transition-colors mx-auto"
//                     >
//                       <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
//                     </button>
//                   </div>
//                 ) : (
             
//                 <div className="divide-y divide-gray-200"> {teamMembers.map((member, index) => ( <div key={member._id} draggable onDragStart={(e) => handleDragStart(e, index)} onDragOver={allowDrop} onDrop={(e) => handleDrop(e, index)} className="p-4 hover:bg-gray-50 transition-colors cursor-move" > <div className="sm:flex justify-between items-center"> <div className="mb-2 sm:mb-0 flex items-center"> {member.photoUrl ? ( <div className="w-12 h-12 rounded-full overflow-hidden mr-4 flex-shrink-0"> <img src={member.photoUrl} alt={member.name} className="w-full h-full object-cover" /> </div> ) : ( <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4 flex-shrink-0"> <Users className="w-6 h-6 text-gray-500" /> </div> )} <div> <h3 className="font-medium text-gray-900">{member.name}</h3> <div className="text-sm text-gray-500 flex flex-wrap items-center mt-1"> <span className="mr-3">{member.position}</span> {member.isLeadership && ( <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium mr-2"> Leadership </span> )} {!member.isActive && ( <span className="bg-gray-200 text-gray-800 px-2 py-0.5 rounded text-xs font-medium"> Inactive </span> )} </div> </div> </div> <button onClick={() => navigate(`/admin/team-members/${member._id}`)} className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center" > Edit <ChevronRight className="w-4 h-4 ml-1" /> </button> </div> </div> ))} </div>
//                 )}
//               </div>
//             </div>
//           ) : (
//             /* Team Member Form View */
//             <div className="container mx-auto px-4 py-6">
//               <div className="bg-white shadow-sm rounded-lg overflow-hidden">
//                 <div className="p-6 border-b border-gray-200">
//                   <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
//                     <div className="flex items-center">
//                       <button
//                         onClick={handleBackToList}
//                         className="mr-3 text-gray-500 hover:text-gray-700 focus:outline-none"
//                       >
//                         <ArrowLeft className="w-5 h-5" />
//                       </button>
//                       <h1 className="text-2xl font-semibold text-gray-800">
//                         {currentMember ? `Edit: ${currentMember.name}` : 'Add New Team Member'}
//                       </h1>
//                     </div>
//                     <div className="flex space-x-2 w-full sm:w-auto justify-end">
//                     {currentMember && (
//                         <button
//                           onClick={deleteTeamMember}
//                           className="bg-red-500 font-normal hover:bg-red-600 text-white px-3 py-1 rounded inline-flex items-center"
//                         >
//                           <Trash2 className="w-4 h-4 mr-1" /> Delete
//                         </button>
//                       )}
//                       <button
//                         onClick={saveTeamMember}
//                         className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded inline-flex items-center"
//                       >
//                         <Save className="w-4 h-4 mr-1" /> Save
//                       </button>
//                     </div>
//                   </div>
//                 </div>
                
//                 {/* Form Content */}
//                 <form onSubmit={saveTeamMember} className="p-6">
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     {/* Left Column - Basic Info */}
//                     <div className="space-y-6">
//                       <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h2>
                      
//                       {/* Photo Upload */}
//                       <div className="mb-6">
//                         <label className="block text-sm font-medium text-gray-700 mb-1">Photo</label>
//                         <div className="flex items-start">
//                           <div className="mr-4">
//                             {formData.photoUrl ? (
//                               <div className="relative group w-24 h-24 rounded-full overflow-hidden border border-gray-200">
//                                 <img 
//                                   src={formData.photoUrl} 
//                                   alt={formData.name} 
//                                   className="w-full h-full object-cover"
//                                 />
//                                 <div 
//                                   className="absolute inset-0 bg-black bg-opacity-40 group-hover:flex items-center justify-center hidden"
//                                   onClick={() => setFormData({...formData, photoUrl: ''})}
//                                 >
//                                   <X className="w-6 h-6 text-white" />
//                                 </div>
//                               </div>
//                             ) : (
//                               <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center">
//                                 <Camera className="w-8 h-8 text-gray-400" />
//                               </div>
//                             )}
//                           </div>
//                           <div className="flex-1">
//                             <ImageUploader 
//                               onImageUpload={handleImageUpload} 
//                               pageSlug="team-members" 
//                             />
//                           </div>
//                         </div>
//                       </div>
                      
//                       {/* Name */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
//                           Name*
//                         </label>
//                         <input
//                           id="name"
//                           type="text"
//                           name="name"
//                           value={formData.name}
//                           onChange={handleInputChange}
//                           required
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         />
//                       </div>
                      
//                       {/* Position */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="position">
//                           Position*
//                         </label>
//                         <input
//                           id="position"
//                           type="text"
//                           name="position"
//                           value={formData.position}
//                           onChange={handleInputChange}
//                           required
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         />
//                       </div>
                      
//                       {/* Department */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="department">
//                           Department
//                         </label>
//                         <input
//                           id="department"
//                           type="text"
//                           name="department"
//                           value={formData.department}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         />
//                       </div>
                      
//                       {/* Email */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
//                           Email
//                         </label>
//                         <input
//                           id="email"
//                           type="email"
//                           name="email"
//                           value={formData.email}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         />
//                       </div>
                      
//                       {/* Phone */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
//                           Phone
//                         </label>
//                         <input
//                           id="phone"
//                           type="text"
//                           name="phone"
//                           value={formData.phone}
//                           onChange={handleInputChange}
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         />
//                       </div>
                      
//                       {/* Flags */}
//                       <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
//                         <label className="flex items-center">
//                           <input 
//                             type="checkbox"
//                             name="isLeadership"
//                             checked={formData.isLeadership}
//                             onChange={handleInputChange}
//                             className="mr-2 h-4 w-4 text-blue-600"
//                           />
//                           <span className="text-sm text-gray-700">Leadership Team</span>
//                         </label>
//                         <label className="flex items-center">
//                           <input 
//                             type="checkbox"
//                             name="isActive"
//                             checked={formData.isActive}
//                             onChange={handleInputChange}
//                             className="mr-2 h-4 w-4 text-blue-600"
//                           />
//                           <span className="text-sm text-gray-700">Active</span>
//                         </label>
//                       </div>
//                     </div>
                    
//                     {/* Right Column - Detailed Info */}
//                     <div className="space-y-6">
//                       <h2 className="text-lg font-medium text-gray-900 border-b pb-2">Detailed Information</h2>
                      
//                       {/* Short Bio */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="shortBio">
//                           Short Bio (max 600 chars)
//                         </label>
//                         <textarea
//                           id="shortBio"
//                           name="shortBio"
//                           value={formData.shortBio}
//                           onChange={handleInputChange}
//                           rows="2"
//                           maxLength="600"
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         ></textarea>
//                         <p className="text-xs text-gray-500 mt-1">
//                           {formData.shortBio?.length || 0}/600 characters
//                         </p>
//                       </div>
                      
//                       {/* Full Bio */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="bio">
//                           Full Bio
//                         </label>
//                         <textarea
//                           id="bio"
//                           name="bio"
//                           value={formData.bio}
//                           onChange={handleInputChange}
//                           rows="6"
//                           className="w-full p-2 border border-gray-300 rounded-md"
//                         ></textarea>
//                       </div>
                      
//                       {/* Social Links */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                           Social Links
//                         </label>
                        
//                         {formData.socialLinks.length > 0 && (
//                           <div className="space-y-2 mb-3">
//                             {formData.socialLinks.map((link, index) => (
//                               <div key={index} className="flex items-center bg-gray-50 p-2 rounded">
//                                 <div className="mr-2 text-gray-500">
//                                   <SocialIcon platform={link.platform} />
//                                 </div>
//                                 <span className="text-sm flex-1 truncate">{link.url}</span>
//                                 <button
//                                   type="button"
//                                   className="text-red-500 hover:text-red-700 p-1"
//                                   onClick={() => removeSocialLink(index)}
//                                 >
//                                   <X className="w-4 h-4" />
//                                 </button>
//                               </div>
//                             ))}
//                           </div>
//                         )}
                        
//                         <div className="flex items-end space-x-2">
//                           <div className="flex-shrink-0">
//                             <label className="block text-xs text-gray-500 mb-1">Platform</label>
//                             <select
//                               value={newSocialLink.platform}
//                               onChange={(e) => setNewSocialLink({...newSocialLink, platform: e.target.value})}
//                               className="p-2 border border-gray-300 rounded-md text-sm"
//                             >
//                               <option value="linkedin">LinkedIn</option>
//                               <option value="twitter">Twitter</option>
//                               <option value="facebook">Facebook</option>
//                               <option value="instagram">Instagram</option>
//                               <option value="github">GitHub</option>
//                               <option value="website">Website</option>
//                               <option value="email">Email</option>
//                             </select>
//                           </div>
//                           <div className="flex-1">
//                             <label className="block text-xs text-gray-500 mb-1">URL</label>
//                             <input
//                               type="text"
//                               value={newSocialLink.url}
//                               onChange={(e) => setNewSocialLink({...newSocialLink, url: e.target.value})}
//                               placeholder="https://..."
//                               className="w-full p-2 border border-gray-300 rounded-md text-sm"
//                             />
//                           </div>
//                           <button
//                             type="button"
//                             onClick={addSocialLink}
//                             className="bg-blue-100 text-blue-700 hover:bg-blue-200 p-2 rounded-md"
//                           >
//                             <Plus className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
                      
//                       {/* Skills */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                           Skills
//                         </label>
                        
//                         <div className="flex flex-wrap gap-2 mb-2">
//                           {formData.skills.map((skill, index) => (
//                             <div 
//                               key={index} 
//                               className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-sm flex items-center"
//                             >
//                               <span>{skill}</span>
//                               <button
//                                 type="button"
//                                 className="ml-1 text-blue-500 hover:text-blue-700"
//                                 onClick={() => removeArrayItem('skills', index)}
//                               >
//                                 <X className="w-4 h-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
                        
//                         <div className="flex">
//                           <input
//                             type="text"
//                             value={newSkill}
//                             onChange={(e) => setNewSkill(e.target.value)}
//                             placeholder="Add a skill"
//                             className="flex-1 p-2 border border-gray-300 rounded-l-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => addArrayItem('skills', newSkill, setNewSkill)}
//                             className="bg-blue-500 text-white px-3 rounded-r-md"
//                           >
//                             <Plus className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
                      
//                       {/* Credentials */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                           Credentials
//                         </label>
                        
//                         <div className="space-y-1 mb-2">
//                           {formData.credentials.map((credential, index) => (
//                             <div 
//                               key={index} 
//                               className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
//                             >
//                               <span>{credential}</span>
//                               <button
//                                 type="button"
//                                 className="text-red-500 hover:text-red-700"
//                                 onClick={() => removeArrayItem('credentials', index)}
//                               >
//                                 <X className="w-4 h-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
                        
//                         <div className="flex">
//                           <input
//                             type="text"
//                             value={newCredential}
//                             onChange={(e) => setNewCredential(e.target.value)}
//                             placeholder="Add a credential (e.g., MBA, CPA)"
//                             className="flex-1 p-2 border border-gray-300 rounded-l-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => addArrayItem('credentials', newCredential, setNewCredential)}
//                             className="bg-blue-500 text-white px-3 rounded-r-md"
//                           >
//                             <Plus className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
                      
//                       {/* Education */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                           Education
//                         </label>
                        
//                         <div className="space-y-1 mb-2">
//                           {formData.education.map((edu, index) => (
//                             <div 
//                               key={index} 
//                               className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
//                             >
//                               <span>{edu}</span>
//                               <button
//                                 type="button"
//                                 className="text-red-500 hover:text-red-700"
//                                 onClick={() => removeArrayItem('education', index)}
//                               >
//                                 <X className="w-4 h-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
                        
//                         <div className="flex">
//                           <input
//                             type="text"
//                             value={newEducation}
//                             onChange={(e) => setNewEducation(e.target.value)}
//                             placeholder="Add education (e.g., BS Computer Science, Harvard)"
//                             className="flex-1 p-2 border border-gray-300 rounded-l-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => addArrayItem('education', newEducation, setNewEducation)}
//                             className="bg-blue-500 text-white px-3 rounded-r-md"
//                           >
//                             <Plus className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
                      
//                       {/* Achievements */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-medium text-gray-700 mb-2">
//                           Achievements
//                         </label>
                        
//                         <div className="space-y-1 mb-2">
//                           {formData.achievements.map((achievement, index) => (
//                             <div 
//                               key={index} 
//                               className="flex justify-between bg-gray-50 p-2 rounded-md text-sm"
//                             >
//                               <span>{achievement}</span>
//                               <button
//                                 type="button"
//                                 className="text-red-500 hover:text-red-700"
//                                 onClick={() => removeArrayItem('achievements', index)}
//                               >
//                                 <X className="w-4 h-4" />
//                               </button>
//                             </div>
//                           ))}
//                         </div>
                        
//                         <div className="flex">
//                           <input
//                             type="text"
//                             value={newAchievement}
//                             onChange={(e) => setNewAchievement(e.target.value)}
//                             placeholder="Add an achievement"
//                             className="flex-1 p-2 border border-gray-300 rounded-l-md"
//                           />
//                           <button
//                             type="button"
//                             onClick={() => addArrayItem('achievements', newAchievement, setNewAchievement)}
//                             className="bg-blue-500 text-white px-3 rounded-r-md"
//                           >
//                             <Plus className="w-5 h-5" />
//                           </button>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
                  
//                   {/* Form Buttons */}
//                   <div className="mt-8 border-t pt-6 flex justify-end">
//                     <button
//                       type="button"
//                       onClick={handleBackToList}
//                       className="bg-gray-200 text-gray-700 hover:bg-gray-300 px-4 py-2 rounded-md mr-2"
//                     >
//                       Cancel
//                     </button>
//                     <button
//                       type="submit"
//                       className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md"
//                     >
//                       {currentMember ? 'Update Team Member' : 'Create Team Member'}
//                     </button>
//                   </div>
//                 </form>
//               </div>
//             </div>
//           )}
//         </>
        
//       )}
//     </div>
//     </div>
//   );
// };

// export default TeamMemberManagement;


